(function initCartChangeTracker() {
  try {
    const TRACKING_ENDPOINT = 'https://discounts-app.fly.dev/api/cart-actions';
    const SHOP_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const JSON_HEADERS = {'Content-Type': 'application/json', 'Accept': 'application/json'};
    const REMOVAL_STORAGE_KEY = 'sleek:manualFreebies';
    const REMOVAL_TTL_MS = 24 * 60 * 60 * 1000; // 24h

    const getGraphQLCart = async () => {
      try {
        const data = await (window.FoxTheme && window.FoxTheme.storefrontClient && window.FoxTheme.storefrontClient.getCartInfo());
        return data && data.cart ? data.cart : null;
      } catch {
        return null;
      }
    };

    const getLinesFromCart = (cart) => {
      if (!cart) return [];
      const lines = cart.lines;
      if (Array.isArray(lines?.nodes)) return lines.nodes;
      if (Array.isArray(lines?.edges)) return lines.edges.map((edge) => edge?.node).filter(Boolean);
      return [];
    };

    const variantIdOfLine = (line) => {
      const merchId = line?.merchandise?.id;
      if (!merchId) return null;
      const variantId = Number(String(merchId).split('/').pop());
      return Number.isFinite(variantId) ? variantId : null;
    };

    const toCents = (amount) => {
      const numeric = Number(amount);
      return Number.isFinite(numeric) ? Math.round(numeric * 100) : 0;
    };

    const discountedUnitsOnLine = (line) => {
      const quantity = Number(line?.quantity || 0);
      if (!(quantity > 0)) return 0;
      if (line?.cost?.totalAmount?.amount == null) return 0;
      const totalCents = toCents(line.cost.totalAmount.amount);
      if (totalCents === 0) return quantity;
      const subtotalCents = toCents(line?.cost?.subtotalAmount?.amount || 0);
      const discountedCents = subtotalCents - totalCents;
      if (discountedCents <= 0) return 0;
      const unitCents = Math.round(subtotalCents / quantity);
      if (!(unitCents > 0)) return 0;
      return Math.max(0, Math.min(quantity, Math.floor(discountedCents / unitCents)));
    };

    const summarizeCart = (cart) => {
      const summary = new Map();
      for (const line of getLinesFromCart(cart)) {
        const variantId = variantIdOfLine(line);
        if (variantId == null) continue;
        const qty = Number(line.quantity);
        if (!Number.isFinite(qty) || qty <= 0) continue;
        const entry = summary.get(variantId) || {qty: 0, freeUnits: 0, line};
        entry.qty += qty;
        entry.freeUnits += discountedUnitsOnLine(line);
        summary.set(variantId, entry);
      }
      return summary;
    };

    const buildVariantDetailFromLine = (line) => {
      if (!line || typeof line !== 'object') return null;
      const merchandise = line.merchandise;
      if (!merchandise || typeof merchandise !== 'object') return null;
      const gid = merchandise.id;
      if (!gid) return null;
      const variantId = Number(String(gid).split('/').pop());
      if (!Number.isFinite(variantId)) return null;

      const product = merchandise.product || {};
      const productHandle = product.handle || '';
      const productTitle = product.title || '';
      const variantTitle = merchandise.title || '';
      const imageUrl = (merchandise.image && merchandise.image.url) || (product.featuredImage && product.featuredImage.url) || '';
      const subtotalAmount = Number(line.cost.subtotalAmount.amount);
      const subtotalCurrency = line.cost.subtotalAmount.currencyCode;
      const qty = Number(line.quantity);
      const price = subtotalAmount / qty;
      const currencyCode = subtotalCurrency;
      const qtyToAdd = 1; // re-adding manually removed freebies should always offer a single unit

      return {
        id: variantId,
        gid,
        title: variantTitle,
        price,
        currencyCode,
        qtyToAdd,
        availableForSale: merchandise.availableForSale !== false,
        product: {
          handle: productHandle,
          title: productTitle,
          imageUrl,
        },
      };
    };

    const readManualRemovals = () => {
      if (!window.localStorage) return {};
      try {
        const raw = window.localStorage.getItem(REMOVAL_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};
        const now = Date.now();
        const cleaned = {};
        let mutated = false;
        for (const [key, value] of Object.entries(parsed)) {
          let expiry = null;
          let detail = null;
          if (value && typeof value === 'object') {
            expiry = Number(value.expiry);
            if (value.detail && typeof value.detail === 'object') {
              detail = value.detail;
            }
          } else {
            expiry = Number(value);
          }
          if (!Number.isFinite(expiry)) {
            mutated = true;
            continue;
          }
          if (expiry > now) {
            cleaned[key] = detail ? {expiry, detail} : {expiry};
          } else {
            mutated = true;
          }
        }
        if (mutated) {
          window.localStorage.setItem(REMOVAL_STORAGE_KEY, JSON.stringify(cleaned));
        }
        return cleaned;
      } catch {
        return {};
      }
    };

    const resetManualRemovals = () => {
      if (!state.manualRemovals || !Object.keys(state.manualRemovals).length) return false;
      state.manualRemovals = {};
      return true;
    };

    const persistManualRemovals = (map) => {
      if (!window.localStorage) return;
      try {
        const serializable = Object.entries(map || {}).reduce((acc, [key, value]) => {
          if (!value) return acc;
          const expiry = Number(value.expiry != null ? value.expiry : value);
          if (!Number.isFinite(expiry)) return acc;
          const entry = {expiry};
          if (value.detail && typeof value.detail === 'object') {
            entry.detail = value.detail;
          }
          acc[key] = entry;
          return acc;
        }, {});
        window.localStorage.setItem(REMOVAL_STORAGE_KEY, JSON.stringify(serializable));
      } catch {}
    };

    const getManualFreebieDetails = () => {
      const now = Date.now();
      let mutated = false;
      const details = [];
      for (const [key, value] of Object.entries(state.manualRemovals)) {
        if (!value) continue;
        const expiry = Number(value.expiry != null ? value.expiry : value);
        if (!Number.isFinite(expiry) || expiry <= now) {
          delete state.manualRemovals[key];
          mutated = true;
          continue;
        }
        if (!state.activeFreebieIds.has(key)) {
          continue;
        }
        if (value.detail && typeof value.detail === 'object') {
          const clone = {...value.detail};
          if (clone.product && typeof clone.product === 'object') {
            clone.product = {...clone.product};
          }
          clone.qtyToAdd = 1;
          details.push(clone);
        }
      }
      if (mutated) {
        persistManualRemovals(state.manualRemovals);
      }
      return details;
    };

    const notifyManualRemovalUpdate = () => {
      const variants = getManualFreebieDetails();
      try {
        document.dispatchEvent(new CustomEvent('cart:manual-freebies', {detail: {variants}}));
      } catch {}
    };

    const getFreebiePickers = () => [...state.freebiePickers];

    const setFreebiePickers = (groups) => {
      const normalized = Array.isArray(groups) ? groups : [];
      const signature = JSON.stringify(normalized);
      if (signature === state.freebiePickersSignature) return false;
      state.freebiePickers = [...normalized];
      state.freebiePickersSignature = signature;
      return true;
    };

    const notifyFreebiePickersUpdate = () => {
      const pickers = getFreebiePickers();
      try {
        document.dispatchEvent(new CustomEvent('cart:freebie-pickers', {detail: {pickers}}));
      } catch {}
    };

    const registerManualRemoval = (variantId, line) => {
      if (!Number.isFinite(variantId)) return false;
      const key = String(variantId);
      const expiry = Date.now() + REMOVAL_TTL_MS;
      const existing = state.manualRemovals[key];
      const detail = buildVariantDetailFromLine(line) || (existing && existing.detail) || null;
      const nextValue = detail ? {expiry, detail} : {expiry};
      if (existing && JSON.stringify(existing) === JSON.stringify(nextValue)) {
        return false;
      }
      state.manualRemovals[key] = nextValue;
      return true;
    };

    const clearManualRemoval = (variantId) => {
      if (!Number.isFinite(variantId)) return false;
      const key = String(variantId);
      if (!(key in state.manualRemovals)) return false;
      delete state.manualRemovals[key];
      return true;
    };

    const noteManualRemovals = (previousCart, currentCart) => {
      const current = summarizeCart(currentCart);

      if (current.size === 0) {
        if (!resetManualRemovals()) return false;
        persistManualRemovals(state.manualRemovals);
        return true;
      }

      if (!previousCart) return false;
      const previous = summarizeCart(previousCart);
      let changed = false;

      for (const [variantId, before] of previous) {
        const after = current.get(variantId);
        const nowQty = after ? after.qty : 0;
        if (nowQty >= before.qty) continue;
        if (before.freeUnits <= 0) continue;
        if (registerManualRemoval(variantId, before.line)) {
          changed = true;
        }
      }

      for (const [variantId, after] of current) {
        const before = previous.get(variantId);
        if (after.qty <= (before ? before.qty : 0)) continue;
        if (clearManualRemoval(variantId)) {
          changed = true;
        }
      }

      if (changed) {
        persistManualRemovals(state.manualRemovals);
      }
      return changed;
    };

    const nonDiscountableProductIds = () => {
      const ids = new Set();
      const push = (value) => {
        const raw = String(value == null ? '' : value).split('/').pop().trim();
        if (raw) ids.add(raw);
      };
      const pfandRules = window.pfandDepositConfig && window.pfandDepositConfig.rules;
      if (Array.isArray(pfandRules)) {
        pfandRules.forEach((rule) => rule && push(rule.depositProductId));
      }
      const excluded = window.discountsExcludedProducts && window.discountsExcludedProducts.products;
      if (Array.isArray(excluded)) {
        excluded.forEach((entry) => push(entry && entry.id != null ? entry.id : entry));
      }
      return Array.from(ids);
    };

    const depositProductIds = () => {
      const rules = window.pfandDepositConfig && window.pfandDepositConfig.rules;
      const ids = new Set();
      if (Array.isArray(rules)) {
        rules.forEach((rule) => {
          const raw = String(rule?.depositProductId ?? '').split('/').pop().trim();
          if (raw) ids.add(raw);
        });
      }
      return Array.from(ids);
    };

    const requestCartActions = async (currentCart, meta) => {
      try {
        const body = {
          current: currentCart,
          localization: {
            country: window.Shopify.country,
          },
          shop: {
            nonDiscountableProductIds: nonDiscountableProductIds(),
            depositProductIds: depositProductIds(),
          },
          meta,
        };
        const response = await fetch(TRACKING_ENDPOINT, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify(body),
        });
        if (!response.ok) return null;
        const json = await response.json();
        if (!json?.success) return null;
        return {
          add: json.add,
          pickers: json.pickers,
          freeShipping: json.freeShipping || null,
        };
      } catch {
        return null;
      }
    };

    window.FoxTheme.cartChangeTracker = window.FoxTheme.cartChangeTracker || {};

    const state = {
      pending: Promise.resolve(),
      isApplyingActions: false,
      lastCart: null,
      manualRemovals: readManualRemovals(),
      activeFreebieIds: new Set(),
      freebiePickers: [],
      freebiePickersSignature: '',
      loadMutationSeen: false,
    };

    window.FoxTheme.cartChangeTracker.getManualFreebies = () => getManualFreebieDetails();
    window.FoxTheme.cartChangeTracker.getFreebiePickers = () => getFreebiePickers();

    notifyManualRemovalUpdate();
    notifyFreebiePickersUpdate();

    const enqueue = (job) => {
      state.pending = state.pending.then(() => job()).catch(() => {});
      return state.pending;
    };

    const isManualRemovalBlocked = (variantId) => {
      if (!Number.isFinite(variantId)) return false;
      const key = String(variantId);
      const value = state.manualRemovals[key];
      if (!value) return false;
      const expiry = Number(value.expiry != null ? value.expiry : value);
      if (!Number.isFinite(expiry) || Date.now() > expiry) {
        delete state.manualRemovals[key];
        persistManualRemovals(state.manualRemovals);
        notifyManualRemovalUpdate();
        return false;
      }
      return true;
    };

    const addFreebieVariants = async (variantIds) => {
      if (!variantIds.length) return false;
      const items = variantIds.map((variantId) => ({id: variantId, quantity: 1}));
      try {
        const response = await fetch(`${SHOP_ROOT}cart/add.js`, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({items}),
        });
        return response.ok;
      } catch {
        // swallow errors to avoid blocking caller; future snapshots may retry
        return false;
      }
    };

    const isCartPage = () => {
      return window.location.pathname === (window.Shopify?.routes?.root + 'cart')
        || window.location.pathname === (window.Shopify?.routes?.root + 'cart/');
    };

    const isAlreadyInCart = (cart, variantId) => {
      for (const line of getLinesFromCart(cart)) {
        if (variantIdOfLine(line) !== variantId) continue;
        if (Number(line.quantity) > 0) return true;
      }
      return false;
    };

    const applyCartActions = async (actions, currentCart) => {
      const addList = actions?.add || [];
      if (!addList.length) return false;

      const blocked = [];
      const allowedAdds = [];
      for (const variantId of addList) {
        if (isManualRemovalBlocked(variantId)) {
          blocked.push(variantId);
        } else if (isAlreadyInCart(currentCart, Number(variantId))) {
          // continue
        } else {
          allowedAdds.push(variantId);
        }
      }

      if (blocked.length) {
        notifyManualRemovalUpdate();
      }

      if (!allowedAdds.length) return false;

      state.isApplyingActions = true;
      try {
        return await addFreebieVariants(allowedAdds);
      } finally {
        state.isApplyingActions = false;
      }
    };

    const syncFreeShipping = async (cart, freeShipping) => {
      const next = freeShipping ? String(freeShipping.code || 'automatic') : '';
      if (next === String(cart?.freeShipping?.value || '')) return;
      state.isApplyingActions = true;
      let updated;
      try {
        updated = await postCart('cart/update.js', {attributes: {_free_shipping: next}});
      } finally {
        state.isApplyingActions = false;
      }
      if (updated) {
        document.dispatchEvent(new CustomEvent('cart:updated', {detail: {cart: updated}}));
      }
    };

    const refreshFreeShipping = async () => {
      const cart = await getGraphQLCart();
      if (!cart) return;
      const actions = await requestCartActions(cart, {source: 'free-shipping', ts: Date.now()});
      if (actions) await syncFreeShipping(cart, actions.freeShipping);
    };

    window.FoxTheme.cartChangeTracker.syncFreeShipping = () => enqueue(refreshFreeShipping);

    const handleCartSnapshot = (source) => enqueue(async () => {
      const current = await getGraphQLCart();
      if (!current) return;

      const manualChanged = noteManualRemovals(state.lastCart, current);
      state.lastCart = current;

      let actions;
      try {
        actions = await requestCartActions(current, {source, ts: Date.now()});
      } catch {
        actions = null;
      }

      let activeSetChanged = false;
      let shouldNotifyPickers = false;
      if (actions) {
        await syncFreeShipping(current, actions.freeShipping);
        setFreebiePickers(actions.pickers);
        shouldNotifyPickers = true;

        const rawVariantIds = Array.isArray(actions.add) ? actions.add : [];
        const nextActive = new Set();
        for (const variantId of rawVariantIds) {
          const numericId = Number(variantId);
          if (Number.isFinite(numericId)) {
            nextActive.add(String(numericId));
          }
        }
        const previousActive = state.activeFreebieIds || new Set();
        let hasChanged = previousActive.size !== nextActive.size;
        if (!hasChanged) {
          for (const id of nextActive) {
            if (!previousActive.has(id)) {
              hasChanged = true;
              break;
            }
          }
        }
        if (!hasChanged) {
          for (const id of previousActive) {
            if (!nextActive.has(id)) {
              hasChanged = true;
              break;
            }
          }
        }
        if (hasChanged) {
          state.activeFreebieIds = nextActive;
          activeSetChanged = true;
        }
      }

      let shouldNotifyManual = manualChanged || activeSetChanged;

      if (actions && actions.add.length) {
        const added = await applyCartActions(actions, current);
        if (added) {
          if (isCartPage()) {
            window.location.reload();
            return;
          }
          const refreshed = await getGraphQLCart();
          if (refreshed) {
            state.lastCart = refreshed;
            shouldNotifyManual = false;
            notifyManualRemovalUpdate();
            try {
              document.dispatchEvent(new CustomEvent('cart:refresh', {detail: {open: false, source: 'cart-change-tracker'}}));
            } catch {}
          } else {
            state.lastCart = null;
          }
        }
      }

      if (shouldNotifyManual) {
        notifyManualRemovalUpdate();
      }
      if (shouldNotifyPickers) {
        notifyFreebiePickersUpdate();
      }
    });

    const isTrackingEndpoint = (url) => {
      try {
        const u = new URL(url, window.location.origin);
        return u.href.indexOf(TRACKING_ENDPOINT) === 0;
      } catch {
        return false;
      }
    };

    const whichAction = (url) => {
      const uStr = url.toString();
      if (/\/cart\/add(\.js)?/.test(uStr)) return 'add';
      if (/\/cart\/change(\.js)?/.test(uStr)) return 'change';
      if (/\/cart\/update(\.js)?/.test(uStr)) return 'update';
      if (/\/cart\/clear(\.js)?/.test(uStr)) return 'clear';
      if (/\/graphql\.json/.test(uStr)) return 'coupon';
      return 'unknown';
    };

    const isCartMutation = (url, method = 'GET', payload = '') => {
      if (!url) return false;
      const m = (method || 'GET').toUpperCase();
      if (m !== 'POST') return false;
      const uStr = url.toString();
      const isCartUpdate = /\/cart\/(add|change|update|clear)(\.js)?/.test(uStr);
      if (isCartUpdate) return true;
      return /\/graphql\.json/.test(uStr) && typeof payload === 'string'
        && payload.includes('mutation cartDiscountCodesUpdate');
    };

    const MAX_LINK_LINES = 8;
    const MAX_LINK_LINE_QTY = 12;
    const MAX_LINK_UNITS = 24;
    const LINK_PARAMS = ['d', 'i'];
    const lineKeyOf = (variantId, planId) => `${variantId}:${planId || ''}`;
    const CODE_RE = /^[\p{L}\p{N}][\p{L}\p{N}._-]{1,39}$/u;
    const parseCodes = (raw) => {
      const codes = (raw || '').split(',').map((code) => code.trim()).filter(Boolean);
      return codes.every((code) => CODE_RE.test(code)) ? codes : [];
    };
    const isShopifyId = (n) => Number.isSafeInteger(n) && n >= 1e9;
    const parseItems = (raw) => {
      const items = new Map();
      let units = 0;
      for (const entry of raw.split(',')) {
        const parts = entry.trim().split(':');
        if (parts.length > 3) return null;
        const id = Number(parts[0]);
        const quantity = parts[1] == null || parts[1] === '' ? 1 : Number(parts[1]);
        const plan = parts[2] == null || parts[2] === '' ? null : Number(parts[2]);
        if (!isShopifyId(id) || !(Number.isInteger(quantity) && quantity > 0)) return null;
        if (plan !== null && !isShopifyId(plan)) return null;
        const key = lineKeyOf(id, plan);
        const total = (items.has(key) ? items.get(key).quantity : 0) + quantity;
        units += quantity;
        if (total > MAX_LINK_LINE_QTY || units > MAX_LINK_UNITS) return null;
        items.set(key, {id, plan, quantity: total});
        if (items.size > MAX_LINK_LINES) return null;
      }
      return items;
    };
    const takeCartSpec = () => {
      const params = new URLSearchParams(window.location.search);
      if (!LINK_PARAMS.some((name) => params.has(name))) return null;
      const codes = parseCodes(params.get('d'));
      const items = params.has('i') ? parseItems(params.get('i') || '') : null;
      const consumed = [codes.length && 'd', items && 'i'].filter(Boolean);
      if (!consumed.length) return null;
      const kept = window.location.search.slice(1).split('&').filter((segment) => {
        const name = segment.split('=')[0];
        return segment && !consumed.includes(name);
      });
      history.replaceState(history.state, '', window.location.pathname + (kept.length ? '?' + kept.join('&') : '') + window.location.hash);
      return {codes, items};
    };
    const getAjaxCart = async () => {
      const response = await fetch(`${SHOP_ROOT}cart.js`, {headers: JSON_HEADERS});
      if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
      return response.json();
    };
    const postCart = async (path, body) => {
      try {
        const response = await fetch(`${SHOP_ROOT}${path}`, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify(body)
        });
        return response.ok ? await response.json().catch(() => ({})) : null;
      } catch {
        return null;
      }
    };
    const applyCartSpec = async ({codes, items}) => {
      let changed = false;
      let cart = null;
      state.isApplyingActions = true;
      try {
        if (items && items.size) {
          const lines = [...items.values()].map((item) => (item.plan
            ? {id: item.id, quantity: item.quantity, selling_plan: item.plan}
            : {id: item.id, quantity: item.quantity}));
          await postCart('cart/add.js', {items: lines});
          changed = true;
        }
        if (codes.length) {
          cart = await postCart('cart/update.js', {discount: codes.join(',')});
          if (cart) changed = true;
        }
      } finally {
        state.isApplyingActions = false;
      }
      if (!changed) return;
      state.lastCart = null;
      if (isCartPage()) {
        await refreshFreeShipping();
        window.location.reload();
        return;
      }
      const pubsub = window.FoxTheme && window.FoxTheme.pubsub;
      if (pubsub && typeof pubsub.publish === 'function') {
        pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, {cart: cart || await getAjaxCart().catch(() => null)});
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', {detail: {open: false}}));
    };
    try {
      const cartSpec = takeCartSpec();
      if (cartSpec) {
        enqueue(() => applyCartSpec(cartSpec));
      }
    } catch (e) {
      console.log('[CartChangeTracker] link params ignored', e);
    }

    // Intercept fetch without blocking the original caller
    const originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = (init && init.method) || (typeof input !== 'string' && input && input.method) || 'GET';
      const payload = init && init.body && typeof init.body === 'string' ? init.body : '';
      const shouldTrack = !state.isApplyingActions && isCartMutation(url, method, payload) && !isTrackingEndpoint(url);
      const p = originalFetch(input, init);

      if (shouldTrack) {
        state.loadMutationSeen = true;
        // Fire-and-forget after the original request resolves
        p.then(() => {
          // Allow Shopify to settle briefly, then snapshot
          setTimeout(async () => {
            handleCartSnapshot('fetch:' + whichAction(url));
          }, 150);
        }).catch(() => {
          // ignore; do not interfere with caller
        });
      }

      return p;
    };

    const runInitialSnapshot = () => {
      if (state.loadMutationSeen) return;
      handleCartSnapshot('page-load');
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(runInitialSnapshot, {timeout: 2000});
    } else {
      setTimeout(runInitialSnapshot, 500);
    }
  } catch (e) {
    console.log('[CartChangeTracker] failed to initialize', e);
  }
})();
